/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { handleProcessClaim, validateField } from "../customSubmitClaim";

import {
    addIntroductionContent,
    addSignatureLegalContent,
    addGeneralInstructionsContent,
    organizeContactSection,
    setupClaimantAccountTypeConditional,
    findField,
    disableEmailValidation,
} from "../common";
import {
    transformTransactionKeys,
    enableThousandsSeparator,
    disableThousandsSeparator,
    formatExistingValues,
    stripThousandsSeparator,
    itemIdOf,
} from "../utils";

/**
 * Validates a form.
 *
 * @param {Object} options - The options object.
 * @param {Event|string} options.event - The event object.
 * @param {CustomSubmitClaim} options.ctx - The current context.
 * @returns {void}
 */
export function validateForm({ event, ctx }) {
    if (event === "init_claim") {
        // Add introduction content from template
        addIntroductionContent();
        // Add signature legal content from template
        addSignatureLegalContent();
        // Add general instructions content from template
        addGeneralInstructionsContent();
        // Organize contact section field order
        organizeContactSection();
        // Move subject_to_backup_withholding field to signature section
        organizeSignatureSection();
        // Setup conditional visibility for "Other (please specify)" field
        setupClaimantAccountTypeConditional();

        ctx.formElement
            .querySelectorAll('.transactions input[type="number"]')
            .forEach(enableThousandsSeparator);

        // Format values loaded from existing claims (loadMember uses requestAnimationFrame)
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                formatExistingValues(ctx.formElement);
            });
        });
    }
    const isPaper = window.location.search.includes("paper_claim=true");
    if (isPaper) disableEmailValidation();
}

/**
 * Processes a claim for a custom member.
 *
 * @param {CustomSubmitClaim} ctx - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(ctx) {
    const claim = handleProcessClaim.call(ctx, validateForm);

    const cleanedTransactions = {};
    Object.entries(claim.data.transactions).forEach(([itemId, row]) => {
        cleanedTransactions[itemId] = {};
        Object.entries(row).forEach(([label, value]) => {
            cleanedTransactions[itemId][label] =
                typeof value === "string"
                    ? stripThousandsSeparator(value)
                    : value;
        });
    });

    const prefixedTransactions = transformTransactionKeys(cleanedTransactions);
    const {
        [itemIdOf(1, 1, 1)]: totalNumberSharesB1,
        [itemIdOf(1, 4, 1)]: totalNumberSharesB4,
        ...restTransactions
    } = prefixedTransactions;

    return {
        ...claim,
        data: {
            ...claim.data,
            contact: {
                ...claim.data.contact,
                ag_PAPER: window.location.search.includes("paper_claim=true"),
            },
            fields: {
                ...claim.data.fields,
                ...totalNumberSharesB1,
                ...totalNumberSharesB4,
                "Claim Form Type": "Individual",
                "Subject to Backup Withholding": ctx.getFormInput(
                    "subject_to_backup_withholding"
                ).checked
                    ? "yes"
                    : "no",
            },
            transactions: restTransactions,
            securities_transactions: [],
        },
    };
}

/**
 * Handles the row added event.
 *
 * @param {HTMLElement} row - The row element.
 */
export function onTransactionRowAdded(row) {
    row.querySelectorAll('input[type="number"]').forEach(
        enableThousandsSeparator
    );
    row.querySelectorAll('input[type="date"]').forEach((input) => {
        input.addEventListener("change", validateField);
    });
}

/**
 * Handles the row removed event.
 *
 * @param {HTMLElement} row - The row element.
 */
export function onTransactionRowRemoved(row) {
    row.querySelectorAll('input[inputmode="decimal"]').forEach(
        disableThousandsSeparator
    );
    row.querySelectorAll('input[type="date"]').forEach((input) => {
        input.removeEventListener("change", validateField);
    });
}

function organizeSignatureSection() {
    const container = document.querySelector("div#signature-legal-description");
    const subjectToBackupWithholding = findField(
        "subject_to_backup_withholding"
    );
    container.appendChild(subjectToBackupWithholding);
}

/**
 * Removes event listeners from number inputs when the form is unloaded.
 *
 * @param {CustomSubmitClaim} ctx - The current context.
 */
export function onFormUnloaded() {
    console.log("[Form Unloaded]");
}
